export const COG_DISPLAY_SIZE_PX = 180;
export const COG_EDGE_PADDING_PX = 16;
export const COG_SPEED_PX_PER_SEC = 13;

export type RandomSource = () => number;

export type CogPosition = {
  x: number;
  y: number;
};

export type CogBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

function finiteDimension(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function normalizedRandom(randomSource: RandomSource): number {
  const value = randomSource();
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(1, Math.max(0, value));
}

export function getCogBounds(input: {
  containerWidth: number;
  containerHeight: number;
  cogWidth?: number;
  cogHeight?: number;
  edgePadding?: number;
}): CogBounds {
  const containerWidth = finiteDimension(input.containerWidth);
  const containerHeight = finiteDimension(input.containerHeight);
  const cogWidth = finiteDimension(input.cogWidth ?? COG_DISPLAY_SIZE_PX);
  const cogHeight = finiteDimension(input.cogHeight ?? COG_DISPLAY_SIZE_PX);
  const requestedPadding = finiteDimension(
    input.edgePadding ?? COG_EDGE_PADDING_PX,
  );
  const horizontalPadding = Math.min(
    requestedPadding,
    Math.max(0, (containerWidth - cogWidth) / 2),
  );
  const verticalPadding = Math.min(
    requestedPadding,
    Math.max(0, (containerHeight - cogHeight) / 2),
  );

  const minX = horizontalPadding;
  const minY = verticalPadding;
  return {
    minX,
    maxX: Math.max(minX, containerWidth - cogWidth - horizontalPadding),
    minY,
    maxY: Math.max(minY, containerHeight - cogHeight - verticalPadding),
  };
}

export function randomCogPosition(input: {
  containerWidth: number;
  containerHeight: number;
  randomSource?: RandomSource;
  cogWidth?: number;
  cogHeight?: number;
  edgePadding?: number;
}): CogPosition {
  const bounds = getCogBounds(input);
  const randomSource = input.randomSource ?? Math.random;
  const randomX = normalizedRandom(randomSource);
  const randomY = normalizedRandom(randomSource);
  return {
    x: bounds.minX + randomX * (bounds.maxX - bounds.minX),
    y: bounds.minY + randomY * (bounds.maxY - bounds.minY),
  };
}

export function clampCogPosition(
  position: CogPosition,
  bounds: CogBounds,
): CogPosition {
  return {
    x: Math.min(bounds.maxX, Math.max(bounds.minX, position.x)),
    y: Math.min(bounds.maxY, Math.max(bounds.minY, position.y)),
  };
}
